<template>
    <div class="tracing-group-card mb-4">
        <!-- Header del grupo (Módulo Origen) -->
        <div 
            class="group-header" 
            :style="{ borderLeftColor: group.color }"
            @click="toggleOriginExpanded"
        >
            <div class="header-left">
                <i 
                    :class="isOriginExpanded ? 'fa fa-caret-down' : 'fa fa-caret-right'"
                    class="toggle-icon"
                ></i>
                <span class="group-label" :style="{ color: group.color }">
                    <i class="fa fa-folder mr-2"></i>
                    {{ group.label }}
                </span>
                <b-badge pill variant="light" class="count-badge ml-2">
                    {{ group.count }} {{ group.count === 1 ? 'plan' : 'planes' }}
                </b-badge>
            </div>
        </div>

        <!-- Lista de Diagnósticos -->
        <b-collapse :visible="isOriginExpanded">
            <div class="diagnostics-container">
                <div 
                    v-for="diagnostic in group.diagnostics" 
                    :key="diagnostic.diagnostic_id"
                    class="diagnostic-group"
                >
                    <!-- Header del Diagnóstico -->
                    <div 
                        class="diagnostic-header"
                        @click="toggleDiagnostic(diagnostic.diagnostic_id)"
                    >
                        <div class="diagnostic-header-left">
                            <i 
                                :class="expandedDiagnosticId === diagnostic.diagnostic_id ? 'fa fa-caret-down' : 'fa fa-caret-right'"
                                class="toggle-icon-sm"
                            ></i>
                            <i class="fa fa-file-text-o diagnostic-icon"></i>
                            <span class="diagnostic-name">{{ diagnostic.diagnostic_name }}</span>
                            <b-badge pill variant="secondary" class="diagnostic-count">
                                {{ diagnostic.count }}
                            </b-badge>
                        </div>
                        <div class="diagnostic-header-right">
                            <span class="diagnostic-date">
                                <i class="fa fa-calendar-o"></i>
                                {{ formatDate(diagnostic.diagnostic_date) }}
                            </span>
                        </div>
                    </div>

                    <!-- Lista de Planes del Diagnóstico -->
                    <b-collapse :visible="expandedDiagnosticId === diagnostic.diagnostic_id">
                        <div class="plans-container">
                            <tracing-plan-item
                                v-for="plan in diagnostic.plans"
                                :key="plan.id"
                                :plan="plan"
                                :status-definitions="statusDefinitions"
                                :priority-definitions="priorityDefinitions"
                                :notification-frequency-definitions="notificationFrequencyDefinitions"
                                :workers="workers"
                                :company-id="companyId"
                                :expanded-plan-id="expandedPlanId"
                                @toggle-plan="togglePlan"
                                @updated="$emit('updated')"
                            />
                        </div>
                    </b-collapse>
                </div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import TracingPlanItem from './TracingPlanItem.vue';

export default {
    name: 'TracingGroupCard',
    components: {
        TracingPlanItem,
    },
    props: {
        group: {
            type: Object,
            required: true,
        },
        statusDefinitions: {
            type: Array,
            default: () => [],
        },
        priorityDefinitions: {
            type: Array,
            default: () => [],
        },
        notificationFrequencyDefinitions: {
            type: Array,
            default: () => [],
        },
        workers: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: Number,
            default: null,
        },
        initialExpanded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOriginExpanded: this.initialExpanded,
            expandedDiagnosticId: null,
            expandedPlanId: null,
        };
    },
    watch: {
        initialExpanded(newVal) {
            this.isOriginExpanded = newVal;
        },
    },
    methods: {
        toggleOriginExpanded() {
            this.isOriginExpanded = !this.isOriginExpanded;
            if (!this.isOriginExpanded) {
                this.expandedDiagnosticId = null;
                this.expandedPlanId = null;
            }
            this.$emit('origin-toggled', this.isOriginExpanded ? this.group.module_origin : null);
        },
        toggleDiagnostic(diagnosticId) {
            if (this.expandedDiagnosticId === diagnosticId) {
                this.expandedDiagnosticId = null;
                this.expandedPlanId = null;
            } else {
                this.expandedDiagnosticId = diagnosticId;
                this.expandedPlanId = null;
            }
        },
        togglePlan(planId) {
            this.expandedPlanId = this.expandedPlanId === planId ? null : planId;
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('es-CO', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
            });
        },
        collapseAll() {
            this.isOriginExpanded = false;
            this.expandedDiagnosticId = null;
            this.expandedPlanId = null;
        },
    },
};
</script>

<style scoped>
.tracing-group-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.group-header {
    padding: 16px 20px;
    background: #f8f9fa;
    border-left: 4px solid #6c757d;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s ease;
}

.group-header:hover {
    background: #e9ecef;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.toggle-icon {
    font-size: 1.1rem;
    color: #6c757d;
    width: 16px;
}

.group-label {
    font-size: 0.95rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.count-badge {
    font-size: 0.75rem;
    background: #e9ecef;
    color: #495057;
}

/* Diagnostic Group Styles */
.diagnostics-container {
    background: #ffffff;
}

.diagnostic-group {
    border-bottom: 1px solid #e9ecef;
}

.diagnostic-group:last-child {
    border-bottom: none;
}

.diagnostic-header {
    padding: 10px 16px 10px 32px;
    background: #ffffff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.2s ease;
}

.diagnostic-header:hover {
    background: #f0f2f4;
}

.diagnostic-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.toggle-icon-sm {
    font-size: 0.9rem;
    color: #6c757d;
    width: 14px;
}

.diagnostic-icon {
    color: #6c757d;
    font-size: 0.9rem;
}

.diagnostic-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
}

.diagnostic-count {
    font-size: 0.7rem;
    padding: 2px 8px;
}

.diagnostic-header-right {
    display: flex;
    align-items: center;
}

.diagnostic-date {
    font-size: 0.8rem;
    color: #6c757d;
    display: flex;
    align-items: center;
    gap: 4px;
}

.diagnostic-date i {
    font-size: 0.75rem;
}

/* Plans Container */
.plans-container {
    padding: 8px 16px 8px 48px;
    background: #ffffff;
}
</style>
